import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Globe, Loader2 } from "lucide-react";
import { getAuthHeader } from "@/services/authService";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface CloudflareSyncButtonProps {
  className?: string;
}

const CloudflareSyncButton: React.FC<CloudflareSyncButtonProps> = ({ className }) => {
  const [isSyncing, setIsSyncing] = useState(false);
  const { toast } = useToast();

  const handleSync = async () => {
    setIsSyncing(true);
    
    try {
      // Call the sync endpoint via API
      const response = await fetch('/api/admin/sync-cloudflare-redirects', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader(),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      
      toast({
        title: "✅ Redirects Synced",
        description: `Successfully synced ${result.count} redirects to Cloudflare. Deploy to activate instant redirects.`,
      });
    } catch (error) {
      console.error('Error syncing redirects:', error);
      toast({
        title: "❌ Sync Failed", 
        description: error instanceof Error ? error.message : "Failed to sync redirects to Cloudflare",
        variant: "destructive",
      });
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            onClick={handleSync}
            disabled={isSyncing}
            className={className}
          >
            {isSyncing ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Globe className="h-4 w-4 mr-2" />
            )}
            Sync to Cloudflare
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>Sync redirects to Cloudflare _redirects file for instant server-side redirects without 404 flashes</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default CloudflareSyncButton; 